import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Link,
} from '@chakra-ui/react';
import { UserAuth } from '../context/AuthContext';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { resetPassword } = UserAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error } = await resetPassword(email);
    setLoading(false);

    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }
  };

  return (
    <Box
      minH="100vh"
      px={4}
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="gray.50"
    >
      <Box
        maxW="md"
        w="full"
        p={6}
        mt={10}
        bg="white"
        rounded="lg"
        shadow="sm"
      >
        <Box textAlign="center" mb={6}>
          <Text fontSize="2xl" fontWeight="bold" color="gray.800">
            Reset Password
          </Text>
          <Text color="gray.600" mt={2}>
            Enter your email to receive a password reset link
          </Text>
        </Box>

        {error && (
          <Alert status="error" mb={4} rounded="md" fontSize="sm">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success ? (
          <Box
            p={4}
            bg="green.50"
            color="green.700"
            rounded="lg"
            textAlign="center"
          >
            <Text fontWeight="semibold">Password reset code sent to {email}</Text>
            <Text mt={2} fontSize="sm">
              Please check your email for the verification code.
            </Text>
            <Button
              as={RouterLink}
              to={`/update-password?email=${encodeURIComponent(email)}`}
              mt={4}
              w="full"
              colorScheme="blue"
              size="sm"
            >
              Enter Code & Reset Password
            </Button>
            <Link
              as={RouterLink}
              to="/signin"
              mt={3}
              display="inline-block"
              color="blue.600"
              fontSize="sm"
              _hover={{ textDecoration: 'underline' }}
              fontWeight="medium"
            >
              Back to Sign In
            </Link>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl id="email" isRequired>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  focusBorderColor="blue.500"
                />
              </FormControl>

              <Button
                type="submit"
                isLoading={loading}
                loadingText="Sending..."
                w="full"
                bgGradient="linear(to-r, blue.600, purple.600)"
                color="white"
                _hover={{ bgGradient: 'linear(to-r, blue.700, purple.700)' }}
                _disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
              >
                Send Reset Link
              </Button>

              <Text fontSize="sm" textAlign="center" mt={4}>
                <Link
                  as={RouterLink}
                  to="/signin"
                  color="blue.600"
                  _hover={{ textDecoration: 'underline' }}
                >
                  Remember your password? Sign In
                </Link>
              </Text>
            </VStack>
          </form>
        )}
      </Box>
    </Box>
  );
};

export default ForgotPassword;
